import type { PlayerStatistics, SaveData } from '@/types';
import { getSupabase } from '@/systems/SupabaseClient';

/**
 * Camada de persistência do save.
 *
 * Abstraímos a implementação por trás de uma interface async pra que
 * SaveSystem não saiba se está falando com Supabase, com memória (testes)
 * ou outra fonte.
 *
 * No PR 4 trocamos Dexie/IndexedDB por Supabase: o save é fonte da verdade
 * online (CLAUDE.md §6 Fase 2 — backend integration). Cada Store é vinculada
 * a um student_id e mapeia 1:1 com a row em public.student_progress.
 */
export interface SaveStore {
  load(): Promise<SaveData | null>;
  save(data: SaveData): Promise<void>;
  clear(): Promise<void>;
}

/* ------------------------------------------------------------------ */
/* MemorySaveStore — testes e fallback (sem rede / sem aluno ativo).  */
/* ------------------------------------------------------------------ */
export class MemorySaveStore implements SaveStore {
  private current: SaveData | null = null;

  async load(): Promise<SaveData | null> {
    return this.current ? { ...this.current } : null;
  }

  async save(data: SaveData): Promise<void> {
    this.current = { ...data };
  }

  async clear(): Promise<void> {
    this.current = null;
  }
}

/* ------------------------------------------------------------------ */
/* SupabaseSaveStore — produção. Vinculada a um student_id; cada       */
/* save é um upsert na row de student_progress correspondente.         */
/* ------------------------------------------------------------------ */

const TABLE = 'student_progress';

interface ProgressRow {
  student_id: string;
  total_xp: number;
  current_rank: number;
  stars_per_level: Record<string, number>;
  statistics: PlayerStatistics | Record<string, never>;
  achievements: string[];
  time_attack_best_score: number;
  last_played_at: string;
}

export class SupabaseSaveStore implements SaveStore {
  constructor(private readonly studentId: string) {}

  async load(): Promise<SaveData | null> {
    const { data, error } = await getSupabase()
      .from(TABLE)
      .select(
        'student_id, total_xp, current_rank, stars_per_level, statistics, achievements, time_attack_best_score, last_played_at',
      )
      .eq('student_id', this.studentId)
      .maybeSingle();

    if (error) throw new Error(`Falha ao carregar progresso: ${error.message}`);
    if (!data) return null;
    return rowToSave(data);
  }

  async save(data: SaveData): Promise<void> {
    if (data.playerId !== this.studentId) {
      // Defesa: nunca gravar progresso de um aluno na row de outro.
      throw new Error('SaveData.playerId não bate com studentId da store');
    }
    const row = saveToRow(data);
    const { error } = await getSupabase().from(TABLE).upsert(row, { onConflict: 'student_id' });
    if (error) throw new Error(`Falha ao salvar progresso: ${error.message}`);
  }

  async clear(): Promise<void> {
    // "Excluir progresso" volta a row pros defaults — não DELETA, pra não
    // disparar regressões em FKs futuras (achievements unlocked etc).
    const { error } = await getSupabase()
      .from(TABLE)
      .update({
        total_xp: 0,
        current_rank: 1,
        stars_per_level: {},
        statistics: {},
        achievements: [],
        time_attack_best_score: 0,
        last_played_at: new Date().toISOString(),
      })
      .eq('student_id', this.studentId);
    if (error) throw new Error(`Falha ao resetar progresso: ${error.message}`);
  }
}

/* ------------------------------------------------------------------ */
/* Mapeamento Supabase row ↔ SaveData                                  */
/* ------------------------------------------------------------------ */

function rowToSave(row: ProgressRow): SaveData {
  // SaveData carrega muito mais do que só student_progress (playerName,
  // avatarId, createdAt, version). Como o jogo agora é multi-aluno e
  // multi-conta, mapeamos de volta com defaults sensatos pros campos que
  // não são persistidos em student_progress — UI já lê displayName do
  // ActiveStudent e a versão é stub local.
  return {
    version: 1,
    playerId: row.student_id,
    playerName: '', // preenchido pela UI a partir do ActiveStudent.displayName
    avatarId: 'default',
    totalXP: row.total_xp,
    currentRank: row.current_rank,
    starsPerLevel: row.stars_per_level ?? {},
    statistics: normalizeStats(row.statistics),
    achievements: row.achievements ?? [],
    createdAt: 0,
    lastPlayedAt: new Date(row.last_played_at).getTime(),
    timeAttackBestScore: row.time_attack_best_score,
  };
}

function saveToRow(data: SaveData): Omit<ProgressRow, 'last_played_at'> & { last_played_at: string } {
  return {
    student_id: data.playerId,
    total_xp: data.totalXP,
    current_rank: data.currentRank,
    stars_per_level: data.starsPerLevel,
    statistics: data.statistics,
    achievements: data.achievements,
    time_attack_best_score: data.timeAttackBestScore,
    last_played_at: new Date(data.lastPlayedAt || Date.now()).toISOString(),
  };
}

/** Garante shape mínimo de PlayerStatistics quando vem `{}` do banco. */
function normalizeStats(raw: PlayerStatistics | Record<string, never>): PlayerStatistics {
  const r = raw as Partial<PlayerStatistics>;
  return {
    totalQuestions: r.totalQuestions ?? 0,
    totalCorrect: r.totalCorrect ?? 0,
    totalCorrectResponseTimeMs: r.totalCorrectResponseTimeMs ?? 0,
    weakestTable: r.weakestTable ?? 1,
    strongestTable: r.strongestTable ?? 1,
    streakRecord: r.streakRecord ?? 0,
    perTable: r.perTable ?? {},
  };
}

/* ------------------------------------------------------------------ */
/* Factory                                                             */
/* ------------------------------------------------------------------ */

/**
 * Cria a store apropriada com base no aluno ativo. Sem aluno = Memory
 * (cenas que tentem persistir antes do ProfileSelect não quebram).
 */
export function createDefaultSaveStore(studentId?: string | null): SaveStore {
  if (!studentId) return new MemorySaveStore();
  return new SupabaseSaveStore(studentId);
}
